import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { useBreadcrumb } from '../../hooks/useBreadcrumb';
import { useBreadcrumbContext } from '../../context/BreadcrumbContext';
import { useContext } from 'react';
import { AppContext } from '../../context/Context';
import { IAppContext } from '../../types';
import { Company } from '../../interfaces/brand';
import { BRAND } from '../../static/constants/brands/brand';
import { PHOTO_DEFINITIONS, PHOTO_SECTIONS, isValidPhotoSectionsByBrand } from '../../context/photoConstants';
import { Photo } from '../../types';
import './BreadcrumbNavigation.scss';

interface BreadcrumbNavigationProps {
  onBreadcrumbClick?: (itemId: string) => void;
  customStyles?: object;
  className?: string;
}

const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({
  onBreadcrumbClick,
  customStyles,
  className = '',
}) => {
  const { visibleBreadcrumbs } = useBreadcrumb();
  const { setActiveBreadcrumbId, activeBreadcrumbId } = useBreadcrumbContext();
  const { state } = useContext(AppContext) as IAppContext;

  // Función para obtener secciones de fotos por breadcrumb (solo para INS)
  const getPhotoSectionsByBreadcrumb = (breadcrumbId: string): string[] => {
    switch (breadcrumbId) {
      case 'initials':
        return ['OTHERS'];
      case 'externas':
        return ['EXTERIOR'];
      default:
        return [];
    }
  };

  // Función para validar si una sección de breadcrumb está completa (solo para INS)
  const isBreadcrumbSectionComplete = (breadcrumbId: string): boolean => {
    if (BRAND !== Company.INS) return false;
    
    const photoSectionIds = getPhotoSectionsByBreadcrumb(breadcrumbId);
    const requiredPhotos = photoSectionIds
      .flatMap(sectionId => PHOTO_SECTIONS[sectionId]?.photoIds || [])
      .filter(photoId => 
        PHOTO_DEFINITIONS[photoId]?.isRequired === true &&
        isValidPhotoSectionsByBrand(
          photoId,
          state.preInspectionModel,
          PHOTO_DEFINITIONS[photoId]?.isVisible,
          BRAND,
        )
      );

    const isValid = requiredPhotos.every(photoId => {
      const photo = state.photos[photoId] as Photo;
      if (!photo) {
        return false;
      }
      
      const hasValidUrl = Boolean(photo.url);
      const hasError = photo.error;

      // Verificar que tenga URL y no tenga errores
      if (!hasValidUrl || hasError) {
        return false;
      }

      // Verificar que la foto ha pasado todas las validaciones correspondientes
      const photoValidationResult = isValidPhotoSectionsByBrand(
        photoId,
        state.preInspectionModel,
        PHOTO_DEFINITIONS[photoId]?.isVisible,
        BRAND,
      );

      return photoValidationResult;
    });

    return isValid;
  };

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, itemId: string) => {
    event.preventDefault();
    console.info(`You clicked breadcrumb: ${itemId}`);
    
    // Actualizar el contexto del breadcrumb
    setActiveBreadcrumbId(itemId);
    
    // Ejecutar callback personalizado si se proporciona
    onBreadcrumbClick?.(itemId);
  };

  // Generar breadcrumbs dinámicamente
  const breadcrumbs = visibleBreadcrumbs.map((item) => {
    const isActive = activeBreadcrumbId === item.id;
    const isComplete = isBreadcrumbSectionComplete(item.id);

    // Determinar el color basado en el estado
    let linkColor = 'inherit';
    if (isActive) {
      linkColor = '#086378'; // Color activo (azul oscuro)
    } else if (isComplete) {
      linkColor = '#086378;'; // Color completado (azul)
    }

    return (
      <Link 
        underline="hover" 
        key={item.id} 
        color="inherit" 
        href={item.href} 
        onClick={(e) => handleClick(e, item.id)}
        sx={{
          color: linkColor,
          fontWeight: isActive ? 'bold' : 'bold',
          opacity: isComplete && !isActive ? 0.8 : 1,
        }}
      >
        {item.label}
      </Link>
    );
  });

  return (
    <Breadcrumbs 
      separator='›'
      aria-label="breadcrumb"
      sx={customStyles}
      className={`breadcrumb-navigation ${className}`}
    >
      {breadcrumbs}
    </Breadcrumbs>
  );
};

export default BreadcrumbNavigation;
